import { ActivatedRoute } from '@angular/router';
import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Base64 } from '../../../../utils/base64';
import { BasePage } from '../../../base.page';
import { ErrorDialog } from '../../../../ui/dialog/error/error.dialog';
import { GetProductUseCase } from '../../../../../core';
import { Id, Product } from '../../../../domain/data';
import { OperativePage } from '../../operative.page';
import { ParentEvents } from '../../../../utils/parent-events.service';

interface ProductDocument {
    title: string;
    url: string;
}

@Component({
    templateUrl: './operative-product-detail.page.html',
    styleUrls: ['./operative-product-detail.page.scss'],
})
export class OperativeProductDetailPage extends BasePage implements AfterViewInit, OnDestroy, OnInit {
    public product: Product;
    public documents: ProductDocument[] = [];
    public showDescription = false;

    constructor(
        operativePage: OperativePage,
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private getProductUseCase: GetProductUseCase,
        private renderer: Renderer2,
        private route: ActivatedRoute
    ) {
        super(parentEvents);
        operativePage.hideHeader();
    }

    ngOnInit() {
        this.getProduct();
        this.renderer.addClass(document.body, 'bg-blue-gradient');
    }

    private getProduct() {
        this.route.params.subscribe((params) => {
            if (params.id) {
                this.getProductUseCase.bind({ productCode: Id.fromString(params.id) }).subscribe((transaction) => {
                    if (transaction.isSuccess) {
                        this.product = transaction.data as Product;
                        this.getDocuments();
                    } else if (!transaction.isAuthError) {
                        this.dialog.open(ErrorDialog, {
                            data: { text: transaction.error },
                        });
                    }
                });
            }
        });
    }

    private getDocuments() {
        if (this.product.documents && this.product.documents.annualReport) {
            this.documents.push({
                title: 'product.detail.annual-accounts',
                url: this.product.documents.annualReport,
            });
        }

        if (this.product.documents && this.product.documents.costAndTaxes) {
            this.documents.push({
                title: 'product.detail.costs-and-taxes',
                url: this.product.documents.costAndTaxes,
            });
        }

        if (this.product.documents && this.product.documents.factsheet) {
            this.documents.push({
                title: 'product.detail.monthly-tab',
                url: this.product.documents.factsheet,
            });
        }

        if (this.product.documents && this.product.documents.fullProspectus) {
            this.documents.push({
                title: 'product.detail.brochure',
                url: this.product.documents.fullProspectus,
            });
        }

        if (this.product.documents && this.product.documents.sustainabilityAnnex) {
            this.documents.push({
                title: 'product.detail.sustainabilityAnnex',
                url: this.product.documents.sustainabilityAnnex,
            });
        }

        if (this.product.documents && this.product.documents.firstHalfFactsheet) {
            this.documents.push({
                title: 'product.detail.semi-annual-report-s1',
                url: this.product.documents.firstHalfFactsheet,
            });
        }

        if (this.product.documents && this.product.documents.secondHalfFactsheet) {
            this.documents.push({
                title: 'product.detail.semi-annual-report-s2',
                url: this.product.documents.secondHalfFactsheet,
            });
        }

        if (this.product.documents && this.product.documents.kIID) {
            this.documents.push({
                title: 'product.detail.kid-dfi',
                url: this.product.documents.kIID,
            });
        }

        if (this.product.documents && this.product.documents.managementRegulation) {
            this.documents.push({
                title: 'product.detail.management-regulation',
                url: this.product.documents.managementRegulation,
            });
        }
    }

    ngOnDestroy() {
        this.renderer.removeClass(document.body, 'bg-blue-gradient');
    }

    toggleDescription() {
        this.showDescription = !this.showDescription;
        this.parentEvents.resize();
    }

    downloadDocument(url: string) {
        if (!url.startsWith('http')) {
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(Base64.toBlob(url));
            } else {
                window.open(URL.createObjectURL(Base64.toBlob(url)), '_blank');
            }
        } else {
            window.open(url, '_blank');
        }
    }
}
